import React, {useEffect, useRef, useState} from 'react';
import useWithStore from "@/Hooks/useWithStore";
import isEmpty from "is-empty";
import {isDesktop, isMobile} from "@/Helpers/Function";
import CatalogCard from "@/Components/Site/CatalogCard";
import Link from "@/Layouts/Link";
import Svg from "@/Components/Svg";
import useViewItemBatcher from "@/Hooks/useViewItemBatcher";
import '@/Pages/Collections/scss/standardTemplateCollection.scss';

let videoTimerId;

const StandardTemplateCollection = (props) => {
    return <section className={`section`}>
        <div className={`collections-lp`}>
            <div className={`container`}>
                <Logo {...props} />
                <Video {...props} />
                <H1 {...props} />
                <H2 {...props} />
                <Description {...props} />
                <SelectionWizardButtons {...props} />
                <Sets {...props} />
                <Buttons {...props} />
                <BottomText {...props} />
            </div>
        </div>
    </section>
}

/**
 * Логотип коллекции
 * @param props
 * @returns {JSX.Element|null}
 * @constructor
 */
const Logo = (props) => {
    const {collectionDetailedData = []} = props;
    const {logoPath = '', logoPathWebp = ''} = collectionDetailedData;

    if (logoPath === '' || logoPathWebp === '') {
        return null;
    }

    return <div className={`collections-lp__logo`}>
        <picture>
            <source type={`image/webp`} srcSet={logoPathWebp} />
            <img src={logoPath} alt={`Logo`} title={`Logo`} loading={`lazy`} />
        </picture>
    </div>
}

/**
 * Видео
 * @param props
 * @returns {JSX.Element|null}
 * @constructor
 */
const Video = (props) => {
    const {collectionDetailedData = []} = props;
    const {videoPosterMobile = '', videoPosterDesktop = '', videoSource = []} = collectionDetailedData;

    const videoRef = useRef(null);

    const [videoSvg, setVideoSvg] = useState(false);
    const [videoPlayPauseFlag, setVideoPlayPauseFlag] = useState(true);
    const [videoVolumeSvg, setVideoVolumeSvg] = useState(true);

    useEffect(() => {
        const video = videoRef.current;

        if (video) {
            video.onended = (e) => {
                video.muted = true;
                setVideoSvg(true);
                setVideoVolumeSvg(true);
            };
        }
    }, [videoRef]);

    const videoTimer = () => setTimeout(() => {
        setVideoPlayPauseFlag(false);
    }, 3000);

    /**
     * Воспроизведение / пауза видео
     */
    const mediaChange = () => {
        try {
            const video = videoRef.current;
            if (video !== null) {
                if (video.paused) {
                    video.play();
                    setVideoSvg(false);
                } else {
                    video.pause();
                    setVideoSvg(true);
                }
                video.onended = (e) => {
                    setVideoSvg(true);
                };
            }
        } catch (e) {
            console.log('e', e);
        }
    }

    /**
     * Убираем кнопку паузы на мобилке
     */
    const mediaChangePlayPause = () => {
        try {
            clearInterval(videoTimerId);
            if (!isDesktop()) {
                if (!videoPlayPauseFlag) {
                    setVideoPlayPauseFlag(true);
                }
                videoTimerId = videoTimer();
            }
        } catch (e) {
            console.log('e', e);
        }
    }

    /**
     * Управление звуком
     */
    const mediaChangeVolume = () => {
        try {
            const video = videoRef.current;
            if (video) {
                if (video.muted) {
                    video.muted = false;
                    setVideoVolumeSvg(false);
                } else {
                    video.muted = true;
                    setVideoVolumeSvg(true);
                }
            }
        } catch (e) {
            console.log('e', e);
        }
    }

    if (videoSource.length === 0) {
        return null;
    }

    return <div className={`collections-lp__video`}>
        <video ref={videoRef}
               autoPlay
               muted={`muted`}
               preload={`none`}
               width={`100%`}
               poster={isMobile() ? videoPosterMobile : videoPosterDesktop}
        >
            {videoSource.map((el) => {
                return <source key={el.id}
                               src={isMobile() ? el.pathMobile : el.pathDesktop}
                               type={el.type} />
            })}
        </video>
        <div className={`video-control`}
             onClick={() => mediaChangePlayPause()}
        >
            {videoSvg ?
                <div className={`gg-play-button`} onClick={() => mediaChange()}></div>
                :
                // <div className={`gg-pause-button`} onClick={() => mediaChange()}></div>
                <>
                    {videoPlayPauseFlag && (
                        <div className={`gg-pause-button`} onClick={() => mediaChange()}></div>
                    )}
                </>
            }
            <div className={`volume`} onClick={() => mediaChangeVolume()}>
                {videoVolumeSvg ?
                    <Svg id={`volumeOffOutlinedIcon`} className={`svg__muted-off`} />
                    :
                    <Svg id={`volumeDownOutlinedIcon`} className={`svg__muted-on`} />
                }
            </div>
        </div>
    </div>
}

/**
 * H1
 * @param props
 * @returns {JSX.Element|null}
 * @constructor
 */
const H1 = (props) => {
    const {collectionDetailedData = []} = props;
    const {h1 = ''} = collectionDetailedData;

    if (isEmpty(h1)) {
        return null;
    }

    return <h1 className={`h1`}>
        {h1}
    </h1>
}

/**
 * H2
 * @param props
 * @returns {JSX.Element|null}
 * @constructor
 */
const H2 = (props) => {
    const {collectionDetailedData = []} = props;
    const {h2 = ''} = collectionDetailedData;

    if (isEmpty(h2)) {
        return null;
    }

    return <h2 className={`h2`}>
        {h2}
    </h2>
}

/**
 * Описание
 * @param props
 * @returns {JSX.Element|null}
 * @constructor
 */
const Description = (props) => {
    const {collectionDetailedData = []} = props;
    const {description = []} = collectionDetailedData;

    if (description.length === 0) {
        return null;
    }

    return <>
        {description.map((el, i) => {
            return <p key={i}
                      className={`collections-lp__description`}>
                {el}
            </p>
        })}
    </>
}

/**
 * Элементы коллекции
 * @param props
 * @returns {JSX.Element|null}
 * @constructor
 */
const Sets = (props) => {
    const {collectionDetailedData = []} = props;
    const {sets = [], isShowMore = false} = collectionDetailedData;

    if (isEmpty(sets)) {
        return null;
    }

    const {registerView} = useViewItemBatcher();
    let cardList = [], cardCount = 0, offset = 0;
    // Сколько прибавлять карточек (показать еще)
    const cardListCountAdd = isMobile() ? 4 : 10;

    const [state, setState] = useState(() => {
        let state = {};
        if (isShowMore) {
            sets.forEach(el => {
                // Сколько показывать карточек при загрузке
                state[`cardListCount_${el.id}`] = isMobile() ? 4 : 10;
                // Количество активных карточек при старте
                state[`cardActive_${el.id}`] = isMobile() ? 4 : 10;
            });
        }
        return state;
    });

    // Добавляем карточки при клике на (показать еще)
    const showMore = (set, setAdd) => {
        setState(state => ({...state, [set]: state[set] + setAdd}));
    }

    // Показать еще текст кнопки
    const showMoreMessage = (count, setAdd) => {
        if (setAdd < count) {
            return `Показать еще ${setAdd} из ${count}`;
        } else {
            return `Показать еще ${count}`;
        }
    };

    return <>
        {sets.map((el) => {
            if (isShowMore) {
                state[`cardActive_${el.id}`] = el.dataCardSet.slice(0, state[`cardListCount_${el.id}`]);
                cardCount = el.dataCardSet.length - state[`cardActive_${el.id}`].length;
                cardList = state[`cardActive_${el.id}`].map((product, i) => {
                    return <CatalogCard
                        key={product.id}
                        el={product}
                        {...props}
                        index={offset + i + 1}
                        registerView={registerView}
                    />
                });
            } else {
                cardList = el.dataCardSet.map((product, i) => {
                    return <CatalogCard
                        key={product.id}
                        el={product}
                        {...props}
                        index={offset + i + 1}
                        registerView={registerView}
                    />
                })
            }
            offset += el.dataCardSet.length;

            return <div key={el.id} className={`collections-lp__set`}>
                <picture>
                    {el.pictureSource.map((el) => {
                        return <source key={el.id}
                                       type={el.type}
                                       srcSet={el.path}
                                       media={el.media}
                        />
                    })}
                    <img src={el.picturePath}
                         alt={el.pictureAlt}
                         title={el.pictureAlt}
                         loading={`lazy`}
                         width={isMobile() ? "768" : "1500"}
                         height={isMobile() ? "768" : "624"}
                    />
                </picture>
                <div className={`setInfo`}>
                    {el.title.map((el, i) => {
                        return <p key={i} className={`setInfo__name`}>
                            {el}
                        </p>
                    })}
                    {el.description.map((el, i) => {
                        return <p key={i} className={`setInfo__description`}>
                            {el}
                        </p>
                    })}
                </div>
                <div className={`cardWrapper`}>
                    {cardList}
                </div>
                {cardCount !== 0 && (
                    <div className={`buttons buttons__showMore`}>
                        <div className={`btn btn__showMore`}
                             onClick={() => showMore(`cardListCount_${el.id}`, cardListCountAdd)}>
                            {showMoreMessage(cardCount, cardListCountAdd)}
                        </div>
                    </div>
                )}
                {el.buttons.map((el) => {
                    return <Link key={el.id} href={el.link} className={el.className}>
                        {el.text}
                    </Link>
                })}
            </div>
        })}
    </>
}

/**
 * Кнопки
 * @param props
 * @returns {JSX.Element|null}
 * @constructor
 */
const Buttons = (props) => {
    const {collectionDetailedData = []} = props;
    const {buttons = []} = collectionDetailedData;

    if (buttons.length === 0) {
        return null;
    }

    return <div className={`collections-lp__buttons`}>
        {buttons.map((el) => {
            return <Link key={el.id} href={el.link} className={el.className}>{el.text}</Link>
        })}
    </div>
}

/**
 * Кнопка Мастер подбора соул-браслета
 * @param props
 * @returns {JSX.Element|null}
 * @constructor
 */
const SelectionWizardButtons = (props) => {
    const {collectionDetailedData = []} = props;
    const {selectionWizardButtons = []} = collectionDetailedData;

    if (selectionWizardButtons.length === 0) {
        return null;
    }

    return <div className={`collections-lp__buttons`}>
        {selectionWizardButtons.map((el) => {
            return <Link key={el.id} href={el.link} className={el.className}>{el.text}</Link>
        })}
    </div>
}

/**
 * Описание в самом низу коллекции
 * @param props
 * @returns {JSX.Element|null}
 * @constructor
 */
const BottomText = (props) => {
    const {collectionDetailedData = []} = props;
    const {bottomText = []} = collectionDetailedData;

    if (bottomText.length === 0) {
        return null;
    }

    return <>
        {bottomText.map((el, i) => {
            return <p key={i}
                      className={`collections-lp__bottomText`}>
                {el}
            </p>
        })}
    </>
}

export default useWithStore(StandardTemplateCollection);
